import { Form, Row, Col } from "react-bootstrap";
import { useFormContext, useWatch } from "react-hook-form";

import { useDirectorySearch } from "../../../hooks/useDirectorySearch";
import ContactPanelForEditPopups from "../../../../../TreatmentPage/components/ContactPanelForEditPopups";
import { formatPhoneNumber } from "../../../../../../Utils/helper";
import StateSelect from "../../insurance/components/StateSelect";
import AutoCompleteSearch from "../../../../../Insurance/AutoCompleteSearch";
import { registerCapitalized } from "../../insurance/components/InsurancePartyFieldset";

const fieldsFor = (prefix) => [
  `${prefix}_dir_id`,
  `${prefix}_first_name`,
  `${prefix}_last_name`,
  `${prefix}_address1`,
  `${prefix}_address2`,
  `${prefix}_city`,
  `${prefix}_state`,
  `${prefix}_zip`,
  `${prefix}_phone`,
  `${prefix}_extension`,
  `${prefix}_fax`,
  `${prefix}_email`,
  `${prefix}_website`,
];

const AttorneyLikeFieldset = ({
  entityId,
  prefix,
  panelName,
  dynamicLabel,
  searchConfig,
  statesData,
}) => {
  const { register, control, setValue } = useFormContext();
  const f = (field) => `counsel.${entityId}.${field}`;
  const search = useDirectorySearch(searchConfig, { entityScope: "counsel", entityId });

  const fields = fieldsFor(prefix);
  const watched = useWatch({ control, name: fields.map(f) });
  const [
    ,
    firstName,
    lastName,
    address1,
    address2,
    city,
    state,
    zip,
    phone,
    ext,
    fax,
    email,
    webiste,
  ] = watched;

  return (
    <>
      <Row className="custom-margin-bottom mx-0 p-l-5 p-r-5 m-t-5">
        <Col md={12} className="p-l-0 p-r-0 position-relative">
          <input
            type="text"
            className="form-control rounded-0 height-25"
            placeholder="Type Counsel Name or Firm Name to add from library"
            value={search.searchTerm}
            onChange={search.onInputChange}
          />
          {search.searchTerm && (
            <span
              onClick={() => search.clearSelection(fields)}
              style={{
                position: "absolute",
                right: 12,
                top: 5,
                cursor: "pointer",
                fontSize: 14,
                lineHeight: 1,
                userSelect: "none",
              }}
            >
              ✕
            </span>
          )}
          {search.results.length > 0 && (
            <AutoCompleteSearch
              searchedInsurances={search.results}
              selectedInsurance={(id) => search.applySelection(id)}
              isFirm
            />
          )}
        </Col>
      </Row>

      <Row className="mx-0 p-l-5 p-r-5">
        <ContactPanelForEditPopups
          name={`${firstName || ""} ${lastName || ""}`.trim()}
          panel_name={panelName}
          pageName="Witnesses"
          className="m-b-5"
          dynamic_label={dynamicLabel}
          phone_number={phone}
          fax_number={fax}
          email={email}
          address1={address1}
          address2={address2}
          city={city}
          state={state}
          zip_code={zip}
          websiteURL={webiste}
          ext={ext}
          buttonData={[]}
        />

        <Col md="auto" className="p-0 d-flex-1">
          <Row className="custom-margin-bottom mx-0">
            <div className="d-flex-1 p-r-5">
              <Form.Control
                type="text"
                placeholder="First Name"
                className="form-control rounded-0 height-25"
                {...registerCapitalized(register, setValue, f(`${prefix}_first_name`))}
              />
            </div>
            <div className="d-flex-1">
              <Form.Control
                type="text"
                placeholder="Last Name"
                className="form-control rounded-0 height-25"
                {...registerCapitalized(register, setValue, f(`${prefix}_last_name`))}
              />
            </div>
          </Row>

          <Row className="custom-margin-bottom mx-0">
            <div className="d-flex-1 p-r-5">
              <Form.Control
                type="text"
                placeholder="Address 1"
                className="form-control rounded-0 height-25"
                {...registerCapitalized(register, setValue, f(`${prefix}_address1`))}
              />
            </div>
            <div className="d-flex-1">
              <Form.Control
                type="text"
                placeholder="Address 2"
                className="form-control rounded-0 height-25"
                {...registerCapitalized(register, setValue, f(`${prefix}_address2`))}
              />
            </div>
          </Row>

          <Row className="custom-margin-bottom mx-0">
            <div className="d-flex-1 p-l-0 p-r-5">
              <Form.Control
                type="text"
                placeholder="City"
                className="form-control rounded-0 height-25"
                {...registerCapitalized(register, setValue, f(`${prefix}_city`))}
              />
            </div>
            <StateSelect name={f(`${prefix}_state`)} statesData={statesData} />
            <div style={{ maxWidth: 90 }} className="d-flex-1">
              <Form.Control
                type="text"
                placeholder="Zip"
                className="form-control rounded-0 height-25"
                {...register(f(`${prefix}_zip`))}
              />
            </div>
          </Row>

          <Row className="custom-margin-bottom mx-0">
            <div className="d-flex-1 p-r-5 p-l-0">
              <Form.Control
                type="text"
                placeholder="(###) ###-####"
                className="form-control rounded-0 height-25"
                {...register(f(`${prefix}_phone`), {
                  onChange: (e) =>
                    setValue(f(`${prefix}_phone`), formatPhoneNumber(e.target.value)),
                })}
              />
            </div>
            <div style={{ maxWidth: 90 }} className="d-flex-1 p-r-5">
              <Form.Control
                type="text"
                placeholder="Ext"
                className="form-control rounded-0 height-25"
                {...register(f(`${prefix}_extension`), {
                  onChange: (e) =>
                    setValue(f(`${prefix}_extension`), e.target.value.replace(/[^0-9]/g, "")),
                })}
              />
            </div>
            <div className="d-flex-1">
              <Form.Control
                type="text"
                placeholder="(###) ###-####"
                className="form-control rounded-0 height-25"
                {...register(f(`${prefix}_fax`), {
                  onChange: (e) => setValue(f(`${prefix}_fax`), formatPhoneNumber(e.target.value)),
                })}
              />
            </div>
          </Row>

          <Row className="custom-margin-bottom mx-0">
            <div className="d-flex-1">
              <Form.Control
                type="text"
                placeholder="Email"
                className="form-control rounded-0 height-25"
                {...register(f(`${prefix}_email`))}
              />
            </div>
          </Row>

          <Row className="custom-margin-bottom mx-0">
            <div className="d-flex-1">
              <Form.Control
                type="text"
                placeholder="Website"
                className="form-control rounded-0 height-25"
                {...register(f(`${prefix}_website`))}
              />
            </div>
          </Row>

          <div className="d-flex align-items-center justify-content-end">
            <button
              onClick={() => search.clearSelection(fields)}
              type="button"
              style={{
                cursor: "pointer",
                fontSize: 14,
                lineHeight: 1,
                userSelect: "none",
                minWidth: "80px",
              }}
              className="btn btn-primary height-25 p-0"
            >
              Clear
            </button>
          </div>
        </Col>
      </Row>
    </>
  );
};

export default AttorneyLikeFieldset;
